import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { type IIntegration } from '@principle-theorem/integrations';
import {
  NG_PRINCIPLE_SHARED_CONFIG,
  OrganisationService,
  type INgPrincipleSharedConfig,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  MapPipe,
  TrackByFunctions,
  isDisabled$,
} from '@principle-theorem/ng-shared';
import {
  PodiumIntegrationStorage,
  createPodiumLink,
  generatePodiumOAuthUrl,
} from '@principle-theorem/principle-core';
import {
  PODIUM_SCOPES,
  type IPodiumIntegrationData,
  type IPodiumSMSLocation,
  type IPodiumSMSLocationsRequest,
  type IPodiumSMSLocationsResponse,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  getDoc,
  httpsCallable,
  isSameRef,
  multiSwitchMap,
  shareReplayCold,
  snapshot,
  toTimestamp,
  type CollectionReference,
  type INamedDocument,
} from '@principle-theorem/shared';
import {
  TemporaryOrgTokenStorage,
  type ITemporaryOrgToken,
} from '@principle-theorem/temporary-tokens';
import { compact, omit } from 'lodash';
import * as moment from 'moment-timezone';
import { ReplaySubject, combineLatest, from, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PodiumLocationInputComponent } from './podium-location-input/podium-location-input.component';
import { type PodiumPracticeLocationFormGroup } from './podium-practice-location-form';

interface IPracticeLocationSummary {
  locationUid: string;
  practice: INamedDocument<IPractice>;
}

@Component({
  selector: 'pr-manage-podium-integration',
  templateUrl: './manage-podium-integration.component.html',
  styleUrls: ['./manage-podium-integration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIcon,
    MapPipe,
    PodiumLocationInputComponent,
  ],
})
export class ManagePodiumIntegrationComponent {
  private _temporaryTokensCol$ = new ReplaySubject<
    CollectionReference<ITemporaryOrgToken>
  >(1);
  private _integrationsCol$ = new ReplaySubject<
    CollectionReference<IIntegration<IPodiumIntegrationData>>
  >(1);
  private _orgUid$ = new ReplaySubject<string>(1);
  private _storage = new PodiumIntegrationStorage();
  podiumStorage = new PodiumIntegrationStorage();
  tokenStorage = new TemporaryOrgTokenStorage();
  trackByIndex = TrackByFunctions.index();
  integrationLink$: Observable<string>;
  enabled$: Observable<boolean>;
  podiumLocations$: Observable<IPodiumSMSLocation[]>;
  mappedLocations$: Observable<IPracticeLocationSummary[]>;

  @Input()
  set integrationsCol(
    integrationsCol: CollectionReference<IIntegration<IPodiumIntegrationData>>
  ) {
    if (!integrationsCol) {
      return;
    }
    this._integrationsCol$.next(integrationsCol);
  }

  @Input()
  set temporaryTokensCol(
    temporaryTokensCol: CollectionReference<ITemporaryOrgToken>
  ) {
    if (!temporaryTokensCol) {
      return;
    }
    this._temporaryTokensCol$.next(temporaryTokensCol);
  }

  @Input()
  set orgUid(orgUid: string) {
    if (!orgUid) {
      return;
    }
    this._orgUid$.next(orgUid);
  }

  constructor(
    @Inject(NG_PRINCIPLE_SHARED_CONFIG) config: INgPrincipleSharedConfig,
    private _dialog: BasicDialogService,
    private _snackBar: MatSnackBar,
    private _organisation: OrganisationService
  ) {
    const scopes = PODIUM_SCOPES;
    const oauthApiUrl = generatePodiumOAuthUrl(config.projectId);
    const temporaryTokenUid$ = combineLatest([
      this._orgUid$,
      this._temporaryTokensCol$,
    ]).pipe(
      switchMap(([orgUid, temporaryTokenCol]) =>
        this.tokenStorage.generate(
          {
            orgUid,
            expiresAt: toTimestamp(moment().add('10', 'minutes')),
            deleteAfter: toTimestamp(moment().add('1', 'hour')),
            deleted: false,
          },
          temporaryTokenCol
        )
      )
    );

    this.integrationLink$ = temporaryTokenUid$.pipe(
      map((temporaryToken) =>
        createPodiumLink({
          scopes,
          oauthApiUrl,
          clientId: config.podium.clientId,
          temporaryTokenUid: temporaryToken.tokenUid,
        })
      ),
      shareReplayCold()
    );

    this.enabled$ = this._integrationsCol$.pipe(
      switchMap((integrationsCol) => this.podiumStorage.get$(integrationsCol)),
      map((integration) => !!integration?.data.oauth)
    );

    const data$ = this._integrationsCol$.pipe(
      switchMap((integrationsCol) => this._storage.get$(integrationsCol)),
      filterUndefined(),
      map((data) => data.data)
    );

    this.mappedLocations$ = data$.pipe(
      map((data) => data.practiceLocations ?? []),
      multiSwitchMap((practiceLocation) =>
        from(getDoc(practiceLocation.practiceRef)).pipe(
          map((practice) => ({
            ...practiceLocation,
            practice,
          }))
        )
      )
    );

    this.podiumLocations$ = this._organisation.organisation$.pipe(
      filterUndefined(),
      switchMap((organisation) =>
        httpsCallable<IPodiumSMSLocationsRequest, IPodiumSMSLocationsResponse>(
          'http-podium-getLocations'
        )({
          orgUid: organisation.ref.id,
        }).pipe(map((response) => response.locations))
      )
    );
  }

  async addPracticeLocation(
    form: PodiumPracticeLocationFormGroup
  ): Promise<void> {
    const practiceLocation = form.getRawValue();
    const col = await snapshot(this._integrationsCol$);
    const practiceLocations =
      (await this._storage.get(col))?.data.practiceLocations ?? [];

    await this._storage.upsert(col, {
      ...practiceLocations,
      practiceLocations: [...practiceLocations, practiceLocation],
    });
    form.reset();
    form.updateValueAndValidity();
  }

  addPracticeLocationDisabled$(
    form: PodiumPracticeLocationFormGroup
  ): Observable<boolean> {
    return isDisabled$(form);
  }

  async removeIntegration(): Promise<void> {
    const confirmed = await this._dialog.confirm({
      prompt:
        'Are you sure you want to disable the Podium integration? No messages will be sent/received while the integration is disabled.',
      title: 'Disable',
      submitLabel: 'Yes, Disable',
      submitColor: 'warn',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    const integrationsCol = await snapshot(this._integrationsCol$);
    const data = (await this._storage.get(integrationsCol))?.data;
    await this.podiumStorage.update(integrationsCol, {
      ...omit(data, 'oauth'),
    });
    this._snackBar.open('Podium disabled');
  }

  async deletePracticeLocation(
    phoneNumber: IPracticeLocationSummary
  ): Promise<void> {
    const confirmed = await this._dialog.confirm({
      prompt:
        'Are you sure you want to delete this? Messages for this Podium location will no longer be able to send/receive with Principle.',
      title: 'Delete',
      submitLabel: 'Yes, Delete',
      submitColor: 'warn',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    const col = await snapshot(this._integrationsCol$);
    const savedLocations =
      (await this._storage.get(col))?.data.practiceLocations ?? [];
    const practiceLocations = compact(
      savedLocations.map((currentLocation) => {
        const isSame =
          currentLocation.locationUid === phoneNumber.locationUid &&
          isSameRef(currentLocation.practiceRef, phoneNumber.practice.ref);
        if (!isSame) {
          return currentLocation;
        }
      })
    );

    await this._storage.upsert(col, {
      practiceLocations,
    });
  }
}
